import { useState } from 'react';
import { X, Save, User, Loader2 } from 'lucide-react';
import { ClientNotesPanel } from '../../components/plugins';

/* ─── Styled input ───────────────────────────────────────────────────────────── */
function StyledInput({ type = 'text', value, onChange, required, maxLength, placeholder, autoComplete }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      maxLength={maxLength}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className="w-full px-4 py-3 rounded-xl text-[15px] font-medium transition-all outline-none"
      style={{
        backgroundColor: 'var(--bg-tertiary, #f3f4f6)',
        border: '1.5px solid var(--border-color, #e5e7eb)',
        color: 'var(--text-primary, #111)',
      }}
      onFocus={e => {
        e.currentTarget.style.borderColor = 'var(--accent-primary, #3b82f6)';
        e.currentTarget.style.background = 'color-mix(in srgb, var(--accent-primary,#3b82f6) 4%, var(--bg-tertiary, #f3f4f6))';
      }}
      onBlur={e => {
        e.currentTarget.style.borderColor = 'var(--border-color, #e5e7eb)';
        e.currentTarget.style.background = 'var(--bg-tertiary, #f3f4f6)';
      }}
    />
  );
}

/* ─── Field row ─────────────────────────────────────────────────────────────── */
function FieldRow({ label, hint, children }) {
  return (
    <div className="py-3.5">
      <label className="block text-[13px] font-semibold uppercase tracking-widest mb-2"
        style={{ color: 'var(--text-secondary, #6b7280)' }}>
        {label}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs" style={{ color: 'var(--text-secondary, #9ca3af)' }}>{hint}</p>}
    </div>
  );
}

/* ─── Avatar ─────────────────────────────────────────────────────────────────── */
function Avatar({ firstName, lastName, size = 'md' }) {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  const sz = size === 'sm'
    ? 'w-8 h-8 text-sm rounded-lg'
    : 'w-14 h-14 text-xl rounded-2xl';
  return (
    <div className={`${sz} flex items-center justify-center font-black flex-shrink-0`}
      style={{
        background: 'linear-gradient(135deg, var(--accent-primary, #3b82f6), color-mix(in srgb, var(--accent-primary,#3b82f6) 60%, var(--accent-info,#06b6d4)))',
        color: '#fff',
        boxShadow: '0 4px 16px color-mix(in srgb, var(--accent-primary,#3b82f6) 30%, transparent)',
      }}>
      {initials || <User style={{ width: size === 'sm' ? 14 : 22, height: size === 'sm' ? 14 : 22 }} />}
    </div>
  );
}

/* ─── Sections ───────────────────────────────────────────────────────────────── */
function PersonalSection({ formData, set, t }) {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('clients.editClient')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
        Update the client's name and email address.
      </p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <div className="py-3.5">
          <label className="block text-[13px] font-semibold uppercase tracking-widest mb-2"
            style={{ color: 'var(--text-secondary, #6b7280)' }}>
            {t('common.name')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <StyledInput
              value={formData.first_name}
              onChange={e => set('first_name', e.target.value)}
              required
              placeholder={t('common.name')}
              autoComplete="given-name"
            />
            <StyledInput
              value={formData.last_name}
              onChange={e => set('last_name', e.target.value)}
              required
              placeholder={t('clients.name')}
              autoComplete="family-name"
            />
          </div>
        </div>
        <FieldRow label={t('common.email')}>
          <StyledInput
            type="email"
            value={formData.email}
            onChange={e => set('email', e.target.value)}
            required
            placeholder="name@example.com"
            autoComplete="email"
          />
        </FieldRow>
      </div>
    </div>
  );
}

function ContactSection({ formData, set, t }) {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('common.phone')} &amp; {t('common.country')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
        Contact details and location for this client.
      </p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <FieldRow label={t('common.phone')}>
          <StyledInput
            type="tel"
            value={formData.phone}
            onChange={e => set('phone', e.target.value)}
            placeholder="+1 555 000 0000"
            autoComplete="tel"
          />
        </FieldRow>
        <FieldRow label={t('common.country')} hint="2-letter ISO code (e.g. US, GB, DE)">
          <div className="relative">
            <StyledInput
              value={formData.country}
              onChange={e => set('country', e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
              placeholder="US"
              autoComplete="country"
            />
            {formData.country?.length === 2 && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl pointer-events-none">
                {String.fromCodePoint(
                  ...[...formData.country.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt(0))
                )}
              </span>
            )}
          </div>
        </FieldRow>
      </div>
    </div>
  );
}

function NotesSection({ client }) {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-1">Notes</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
        Internal notes and comments for this client.
      </p>
      <ClientNotesPanel userId={client.id} />
    </div>
  );
}

function SectionContent({ activeSection, formData, set, t, client, clientNotesEnabled }) {
  if (activeSection === 'personal') return <PersonalSection formData={formData} set={set} t={t} />;
  if (activeSection === 'contact')  return <ContactSection  formData={formData} set={set} t={t} />;
  if (activeSection === 'notes' && clientNotesEnabled) return <NotesSection client={client} />;
  return null;
}

/* ─── Main modal ─────────────────────────────────────────────────────────────── */
export default function ClientEditModal({ client, onClose, onSave, t, clientNotesEnabled }) {
  const [formData, setFormData] = useState({
    first_name: client.first_name || '',
    last_name:  client.last_name  || '',
    email:      client.email      || '',
    phone:      client.phone      || '',
    country:    client.country    || '',
  });
  const [saving, setSaving]           = useState(false);
  const [activeSection, setActiveSection] = useState('personal');

  const SECTIONS = [
    { id: 'personal', label: t('common.name'),  icon: '👤' },
    { id: 'contact',  label: t('common.phone'), icon: '📞' },
    ...(clientNotesEnabled ? [{ id: 'notes', label: 'Notes', icon: '📝' }] : []),
  ];

  const set = (key, val) => setFormData(p => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    setSaving(true);
    try { await onSave(formData); } finally { setSaving(false); }
  };

  const fullName = [formData.first_name, formData.last_name].filter(Boolean).join(' ') || '—';

  return (
    <>
      <style>{`
        /* ── Mobile: sheet from bottom ── */
        .cem-backdrop { align-items: flex-end; }
        .cem-dialog {
          width: 100%; border-radius: 20px 20px 0 0;
          max-height: 92dvh; flex-direction: column;
          animation: cemSlideUp .28s cubic-bezier(.32,.72,0,1);
        }
        .cem-handle  { display: flex; }
        .cem-sidebar { display: none; }
        .cem-tabbar  { display: flex; }
        .cem-footer  { padding: 12px 16px; padding-bottom: max(16px, env(safe-area-inset-bottom)); }
        .cem-footer button { padding-top: 14px; padding-bottom: 14px; border-radius: 16px; font-size: 16px; font-weight: 600; }

        /* ── Desktop: centered dialog with sidebar ── */
        @media (min-width: 768px) {
          .cem-backdrop { align-items: center; justify-content: center; padding: 16px; }
          .cem-dialog {
            width: 100%; max-width: 780px; border-radius: 16px;
            max-height: 90vh; flex-direction: row;
            border: 1px solid var(--border-color);
            animation: cemFadeIn .22s ease;
          }
          .cem-handle  { display: none; }
          .cem-sidebar { display: flex; }
          .cem-tabbar  { display: none; }
          .cem-footer  { padding: 24px; }
          .cem-footer button { padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 500; }
        }

        @keyframes cemSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes cemFadeIn {
          from { transform: translateY(6px) scale(0.985); opacity: 0; }
          to   { transform: translateY(0)   scale(1);     opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="cem-backdrop fixed inset-0 z-50 flex"
        style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <div className="cem-dialog flex overflow-hidden" style={{ background: 'var(--bg-primary, #fff)' }}>

          {/* ════ Desktop sidebar ════════════════════════════════════════ */}
          <div className="cem-sidebar w-52 flex-col flex-shrink-0 overflow-y-auto p-4"
            style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)' }}>

            {/* Client identity card */}
            <div className="flex flex-col items-center text-center px-2 pt-3 pb-5 mb-3 border-b"
              style={{ borderColor: 'var(--border-color)' }}>
              <Avatar firstName={formData.first_name} lastName={formData.last_name} />
              <p className="mt-3 text-sm font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
                {fullName}
              </p>
              <p className="mt-0.5 text-[11px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                #{client.id}
              </p>
            </div>

            <h3 className="text-xs font-semibold mb-2 px-2 uppercase tracking-wider"
              style={{ color: 'var(--text-secondary)' }}>
              Sections
            </h3>
            <div className="space-y-1">
              {SECTIONS.map(s => (
                <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2"
                  style={{
                    background: activeSection === s.id ? 'var(--accent-primary)' : 'transparent',
                    color: activeSection === s.id ? '#fff' : 'var(--text-secondary)',
                  }}>
                  <span>{s.icon}</span>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* ════ Main column ════════════════════════════════════════════ */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">

            {/* Mobile drag handle */}
            <div className="cem-handle justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-[5px] rounded-full" style={{ background: 'var(--border-color, #d1d5db)' }} />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
              style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
              <div className="flex items-center gap-3">
                {/* Avatar shown on mobile only (sidebar hidden) */}
                <div className="cem-handle">
                  <Avatar firstName={formData.first_name} lastName={formData.last_name} size="sm" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{fullName}</h2>
                  <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {t('clients.editClient')} · #{client.id}
                  </p>
                </div>
              </div>
              <button onClick={onClose}
                className="p-2 rounded-lg transition-colors"
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile tab bar */}
            <div className="cem-tabbar gap-1 px-3 py-2 flex-shrink-0 overflow-x-auto"
              style={{ borderBottom: '1px solid var(--border-color)' }}>
              {SECTIONS.map(({ id, label, icon }) => (
                <button key={id} type="button" onClick={() => setActiveSection(id)}
                  className="flex-shrink-0 flex items-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition-all active:scale-95"
                  style={{
                    background: activeSection === id ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-secondary, #f3f4f6)',
                    color: activeSection === id ? '#fff' : 'var(--text-secondary, #6b7280)',
                  }}>
                  <span>{icon}</span>
                  {label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5" style={{ WebkitOverflowScrolling: 'touch' }}>
              <SectionContent
                activeSection={activeSection}
                formData={formData}
                set={set}
                t={t}
                client={client}
                clientNotesEnabled={clientNotesEnabled}
              />
              <div className="h-2" />
            </div>

            {/* Footer */}
            <div className="cem-footer flex gap-3 flex-shrink-0"
              style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
              <button type="button" onClick={onClose} disabled={saving}
                className="flex-1 transition-colors disabled:opacity-50"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                onMouseEnter={e => !saving && (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-secondary)')}>
                {t('common.cancel')}
              </button>
              <button type="button" onClick={handleSubmit} disabled={saving}
                className="flex-1 text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ background: 'var(--accent-primary, #3b82f6)' }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? t('settings.saving') : t('common.save')}
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}